import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';
import {compactEmptiedPropertyFilterRows, getMatchingEntityIds} from './evaluateEntityPropertyFilters';

/**
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isNonEmptyPropertyItem(item) {
    const fields = [item?.value, item?.valueLabel, item?.valueEntityId];
    return fields.some((field) => typeof field === 'string' && field.trim());
}

/**
 * Preferred display/stored token for a KB property item.
 * @param {object|null|undefined} item
 * @returns {string|null}
 */
export function primaryValueTokenForItem(item) {
    if (typeof item?.valueLabel === 'string' && item.valueLabel.trim()) {
        return item.valueLabel.trim();
    }
    if (typeof item?.value === 'string' && item.value.trim()) {
        return item.value.trim();
    }
    if (typeof item?.valueEntityId === 'string' && item.valueEntityId.trim()) {
        return item.valueEntityId.trim();
    }
    return null;
}

function propertyKeyForItem(item) {
    if (typeof item?.propertyId === 'string' && item.propertyId.trim()) {
        return item.propertyId.trim();
    }
    if (typeof item?.propertyLabel === 'string' && item.propertyLabel.trim()) {
        return item.propertyLabel.trim();
    }
    return null;
}

function normalizeComparableString(raw) {
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
}

function addDistinctToken(tokenMap, token) {
    const key = normalizeComparableString(token);
    if (!key || tokenMap.has(key)) return;
    tokenMap.set(key, token);
}

/**
 * Entity ids matching all complete filter rows except excludeRowId.
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @param {Record<string, Array<object>>} propertiesByEntityId
 * @param {Record<string, { entityType?: string|null }>} summariesByEntityId
 * @param {Array<object>} filters
 * @param {string|null|undefined} excludeRowId
 * @returns {Set<string>}
 */
export function getFacetEligibleEntityIds(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    excludeRowId,
    {summariesLoading = false} = {},
) {
    const otherFilters = (filters || []).filter((row) => row?.id !== excludeRowId);
    return getMatchingEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        otherFilters,
        {summariesLoading},
    );
}

/**
 * Distinct entity type tokens among facet-eligible entities.
 *
 * @returns {Array<{ token: string, label: string }>}
 */
export function getEntityTypeSuggestions(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    excludeRowId,
    {summariesLoading = false} = {},
) {
    const eligibleIds = getFacetEligibleEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        filters,
        excludeRowId,
        {summariesLoading},
    );
    const tokenMap = new Map();

    for (const entity of chartEntities || []) {
        const entityId = entity?.wikidataId;
        if (!entityId || !eligibleIds.has(entityId)) continue;
        const entityType = summariesByEntityId?.[entityId]?.entityType;
        if (!entityType) continue;
        addDistinctToken(tokenMap, entityType);
    }

    return Array.from(tokenMap.values())
        .sort((a, b) => getEntityTypeLabel(a).localeCompare(getEntityTypeLabel(b), undefined, {sensitivity: 'base'}))
        .map((token) => ({
            token,
            label: getEntityTypeLabel(token) || token,
        }));
}

/**
 * KB properties with at least one non-empty value among facet-eligible entities.
 *
 * @returns {Array<{ propertyKey: string, label: string }>}
 */
export function getFacetedPropertyOptions(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    excludeRowId,
    allPropertyOptions = [],
    {summariesLoading = false} = {},
) {
    const eligibleIds = getFacetEligibleEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        filters,
        excludeRowId,
        {summariesLoading},
    );
    const propertyKeysWithValues = new Set();

    for (const entityId of eligibleIds) {
        for (const item of propertiesByEntityId?.[entityId] || []) {
            const propertyKey = propertyKeyForItem(item);
            if (!propertyKey || !isNonEmptyPropertyItem(item)) continue;
            propertyKeysWithValues.add(propertyKey);
        }
    }

    return (allPropertyOptions || []).filter((option) => (
        propertyKeysWithValues.has(option.propertyKey)
    ));
}

/**
 * Distinct value tokens for a property among facet-eligible entities.
 *
 * @returns {string[]}
 */
export function getPropertyValueSuggestions(
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    filters,
    excludeRowId,
    propertyKey,
    {summariesLoading = false} = {},
) {
    const trimmedKey = typeof propertyKey === 'string' ? propertyKey.trim() : '';
    if (!trimmedKey) return [];

    const eligibleIds = getFacetEligibleEntityIds(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        filters,
        excludeRowId,
        {summariesLoading},
    );
    const tokenMap = new Map();

    for (const entityId of eligibleIds) {
        for (const item of propertiesByEntityId?.[entityId] || []) {
            if (propertyKeyForItem(item) !== trimmedKey) continue;
            if (!isNonEmptyPropertyItem(item)) continue;
            const token = primaryValueTokenForItem(item);
            if (token) addDistinctToken(tokenMap, token);
        }
    }

    return Array.from(tokenMap.values()).sort((a, b) => (
        a.localeCompare(b, undefined, {sensitivity: 'base'})
    ));
}

/**
 * Remove chip values that are no longer valid for their row's exclude-self facet pool.
 *
 * @param {Array<object>} filters
 * @returns {Array<object>}
 */
export function pruneStaleFilterValues(
    filters,
    chartEntities,
    propertiesByEntityId,
    summariesByEntityId,
    {summariesLoading = false} = {},
) {
    const mapped = (filters || []).map((row) => {
        if (row?.kind === 'entityType') {
            if (summariesLoading) return row;
            const allowed = new Set(
                getEntityTypeSuggestions(
                    chartEntities,
                    propertiesByEntityId,
                    summariesByEntityId,
                    filters,
                    row.id,
                ).map((option) => normalizeComparableString(option.token)),
            );
            const values = (Array.isArray(row.values) ? row.values : [])
                .filter((value) => allowed.has(normalizeComparableString(value)));
            return {...row, values};
        }

        if (row?.kind === 'property' || row?.propertyKey != null) {
            const propertyKey = typeof row?.propertyKey === 'string' ? row.propertyKey.trim() : '';
            const allowed = new Set(
                getPropertyValueSuggestions(
                    chartEntities,
                    propertiesByEntityId,
                    summariesByEntityId,
                    filters,
                    row.id,
                    propertyKey,
                    {summariesLoading},
                ).map((value) => normalizeComparableString(value)),
            );
            const rawValues = Array.isArray(row.values)
                ? row.values
                : (row?.value != null && String(row.value).trim() ? [String(row.value).trim()] : []);
            const values = rawValues.filter((value) => allowed.has(normalizeComparableString(value)));
            return {
                ...row,
                kind: 'property',
                values,
            };
        }

        return row;
    });
    return compactEmptiedPropertyFilterRows(mapped, filters);
}
